import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  criarSolicitacao,
  minhasSolicitacoes,
  solicitacoesPendentes,
  aprovarSolicitacao as aprovarApi,
  negarSolicitacao as negarApi,
  decidirSupervisor as decidirSupervisorApi,
  decidirSolicitacoesLote,
  type SolicitacaoTransferencia,
  type ConsultaSolicitacoes,
} from '@/lib/aprovacoesApi';

// Fluxo de solicitação de transferência de carteira — agora 100% via REST
// endurecido no backend (papel do aprovador, transação, notificações).
// Mesma equipe: supervisor decide sozinho. Entre equipes: supervisor
// encaminha -> gerência decide. A aprovação transfere pelo caminho oficial
// (donos_cliente) — pedidos e comissões nunca são tocados.

function comAliases(row: SolicitacaoTransferencia) {
  return {
    ...row,
    cliente: row.clientes ?? null,
    solicitante: row.usuarios_solicitacao_avaliacao_solicitante_idTousuarios ?? null,
    avaliador: row.usuarios_solicitacao_avaliacao_avaliador_idTousuarios ?? null,
  };
}

export function useSolicitacaoAvaliacao() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const useMinhasSolicitacoes = (userId?: string, opcoes: ConsultaSolicitacoes = {}, atualizar = false) => {
    return useQuery({
      queryKey: ['minhas-solicitacoes', userId, opcoes],
      queryFn: async () => (await minhasSolicitacoes(opcoes)).map(comAliases),
      enabled: !!userId,
      refetchInterval: userId && atualizar ? 30_000 : false,
      staleTime: 15_000,
    });
  };

  const useSolicitacoesPendentes = (habilitado: boolean, opcoes: ConsultaSolicitacoes = {}, userId?: string) => {
    return useQuery({
      queryKey: ['solicitacoes-pendentes', userId, opcoes],
      queryFn: async () => (await solicitacoesPendentes(opcoes)).map(comAliases),
      enabled: habilitado,
      staleTime: 5_000,
      refetchInterval: habilitado ? 30_000 : false,
      refetchOnWindowFocus: true,
    });
  };

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['timeline-vinculos'] });
    queryClient.invalidateQueries({ queryKey: ['minhas-solicitacoes'] });
    queryClient.invalidateQueries({ queryKey: ['solicitacoes-pendentes'] });
    queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'carteira-clientes' });
    queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'busca-360' });
  };

  const createSolicitacaoMutation = useMutation({
    mutationFn: async (params: { clienteId: string; solicitanteId?: string; justificativa: string; origem?: string }) =>
      criarSolicitacao({ clienteId: params.clienteId, justificativa: params.justificativa, origem: params.origem }),
    onSuccess: (sol) => {
      invalidar();
      toast({
        title: sol ? 'Solicitação enviada' : 'Transferência concluída',
        description: sol ? 'Acompanhe em Busca 360 → Solicitações e aprovações.' : 'O cliente foi transferido conforme a regra de dono lead.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Não foi possível solicitar',
        description: error?.message || 'Erro ao enviar solicitação',
        variant: 'destructive',
      });
    },
  });

  const aprovarSolicitacaoMutation = useMutation({
    mutationFn: async (params: { solicitacaoId: string; avaliadorId?: string; resposta: string }) =>
      aprovarApi(params.solicitacaoId, params.resposta),
    onSuccess: () => {
      invalidar();
      toast({
        title: 'Solicitação aprovada',
        description: 'Carteira transferida. Leads em aberto seguiram o novo dono; pedidos ficam com quem vendeu.',
      });
    },
    onError: (error: any) => {
      invalidar();
      toast({ title: 'Erro ao aprovar', description: error?.name === 'TimeoutError' ? 'A resposta demorou. Confira a situação atualizada da solicitação antes de tentar novamente.' : error?.message, variant: 'destructive' });
    },
  });

  const negarSolicitacaoMutation = useMutation({
    mutationFn: async (params: { solicitacaoId: string; avaliadorId?: string; resposta: string }) =>
      negarApi(params.solicitacaoId, params.resposta),
    onSuccess: () => {
      invalidar();
      toast({ title: 'Solicitação negada' });
    },
    onError: (error: any) => {
      invalidar();
      toast({ title: 'Erro ao negar', description: error?.name === 'TimeoutError' ? 'A resposta demorou. Confira a situação atualizada da solicitação antes de tentar novamente.' : error?.message, variant: 'destructive' });
    },
  });

  const decidirSupervisorMutation = useMutation({
    mutationFn: async (params: { solicitacaoId: string; aprovar: boolean; justificativa: string }) =>
      decidirSupervisorApi(params.solicitacaoId, params.aprovar, params.justificativa),
    onSuccess: (_data, vars) => {
      invalidar();
      toast({
        title: vars.aprovar ? 'Etapa do supervisor concluída' : 'Solicitação negada',
        description: vars.aprovar
          ? 'Se a troca é na mesma equipe, já foi transferida; entre equipes, seguiu para a gerência.'
          : undefined,
      });
    },
    onError: (error: any) => {
      invalidar();
      toast({ title: 'Erro na decisão', description: error?.name === 'TimeoutError' ? 'A resposta demorou. Confira a situação atualizada da solicitação antes de tentar novamente.' : error?.message, variant: 'destructive' });
    },
  });

  const decidirLoteMutation = useMutation({
    mutationFn: (params: { ids: string[]; acao: 'aprovar' | 'negar'; resposta: string }) =>
      decidirSolicitacoesLote(params.ids, params.acao, params.resposta),
    onSuccess: (data, vars) => {
      invalidar();
      toast({ title: `${data.quantidade} solicitações ${vars.acao === 'aprovar' ? 'aprovadas' : 'negadas'}` });
    },
    onError: (error: unknown) => {
      invalidar();
      toast({ title: 'Erro na decisão em lote', description: error instanceof Error ? error.message : 'Não foi possível concluir o lote.', variant: 'destructive' });
    },
  });

  const createSolicitacao = useCallback(
    (clienteId: string, _solicitanteId: string, justificativa: string, origem?: string) => {
      return createSolicitacaoMutation.mutateAsync({ clienteId, justificativa, origem });
    },
    [createSolicitacaoMutation],
  );

  const aprovarSolicitacao = useCallback(
    (solicitacaoId: string, _avaliadorId: string, resposta: string) => {
      aprovarSolicitacaoMutation.mutate({ solicitacaoId, resposta });
    },
    [aprovarSolicitacaoMutation],
  );

  const negarSolicitacao = useCallback(
    (solicitacaoId: string, _avaliadorId: string, resposta: string) => {
      negarSolicitacaoMutation.mutate({ solicitacaoId, resposta });
    },
    [negarSolicitacaoMutation],
  );

  const decidirSupervisor = useCallback(
    (solicitacaoId: string, aprovar: boolean, justificativa: string) => {
      decidirSupervisorMutation.mutate({ solicitacaoId, aprovar, justificativa });
    },
    [decidirSupervisorMutation],
  );

  return {
    useMinhasSolicitacoes,
    useSolicitacoesPendentes,
    createSolicitacao,
    aprovarSolicitacao,
    negarSolicitacao,
    decidirSupervisor,
    decidirLote: decidirLoteMutation.mutateAsync,
    isCreating: createSolicitacaoMutation.isPending,
    isApproving: aprovarSolicitacaoMutation.isPending,
    isDenying: negarSolicitacaoMutation.isPending,
    isDecidingSupervisor: decidirSupervisorMutation.isPending,
    isDecidingLote: decidirLoteMutation.isPending,
  };
}
